



























































































































































































































































































































































































































































































import { Component, Vue } from 'vue-property-decorator'
import ApiClient from '@/services/api-client'
import { Account, AccountFilters, AccountView, AccountViewType, CvValue, Company } from '@/models'
import UpsertAccountModal from '@/components/modals/UpsertAccountModal.vue'
import MergeAccountsModal from '@/components/modals/MergeAccountsModal.vue'
import LdapImportAccountsModal from '@/components/modals/LdapImportAccountsModal.vue'
import LdapImportModal from '@/components/modals/LdapImportModal.vue'
import { CvValueUtils } from '@/utils/cv-value-utils'
import { AccountUtils } from '@/utils/account-utils'
import { ACCOUNT_COLUMNS, defaultVisibleColumnKeys, orderedVisibleColumns } from '@/utils/account-columns'
import { CV_VALUE_COUNTRY, CV_VALUE_EDITORIAL_PROJECT_BUSINESS_UNIT, ACCOUNT_PROFILE_ADMIN, ACCOUNT_PROFILE_MANAGER } from '@/utils/constants'
import store from '@/store'

function defaultAccountFilters(): AccountFilters {
  return {
    search: '',
    profile: null,
    country: null,
    businessUnit: null,
    active: null,
    editorialStatus: null,
    company: null
  }
}

@Component({
  components: {
    UpsertAccountModal,
    MergeAccountsModal,
    LdapImportAccountsModal,
    LdapImportModal
  }
})
export default class Accounts extends Vue {
  private accounts: Account[] = []
  private filters: AccountFilters = defaultAccountFilters()
  private loading = false
  private searchTimeout: any = null
  private currentPage = 1
  private perPage = 25
  private totalResults = 0
  private selectedAccount: Account | null = null
  private countries: CvValue[] = []
  private businessUnits: CvValue[] = []
  private companies: Company[] = []
  private savedViews: AccountView[] = []
  private selectedFilterViewId: number | null = null
  private newViewName = ''
  private pendingSaveType: AccountViewType = 'filter'
  private visibleColumnKeys: string[] = defaultVisibleColumnKeys()
  private columnPickerSearch = ''
  private sortBy = ''
  private sortDesc = false
  private ACCOUNT_PROFILE_ADMIN = ACCOUNT_PROFILE_ADMIN
  private ACCOUNT_PROFILE_MANAGER = ACCOUNT_PROFILE_MANAGER

  get fields() {
    const cols: any[] = orderedVisibleColumns(this.visibleColumnKeys).map(col => ({
      key: col.key,
      label: this.$t(col.labelKey).toString(),
      sortable: col.sortable,
      stickyColumn: col.sticky === true,
      class: col.cellClass
    }))
    cols.push({
      key: 'actions',
      label: this.$t('table.actions').toString(),
      sortable: false,
      class: 'text-center'
    })
    return cols
  }

  get filteredColumns() {
    const term = this.columnPickerSearch.trim().toLowerCase()
    if (!term) return ACCOUNT_COLUMNS
    return ACCOUNT_COLUMNS.filter(col =>
      this.$t(col.labelKey).toString().toLowerCase().includes(term)
    )
  }

  get savedFilterViews(): AccountView[] {
    return this.savedViews.filter(v => v.type === 'filter')
  }

  get savedColumnViews(): AccountView[] {
    return this.savedViews.filter(v => v.type === 'columns')
  }

  get selectedFilterViewIsDefault(): boolean {
    return !!this.savedViews.find(v => v.id === this.selectedFilterViewId)?.isDefault
  }

  get saveModalTitle(): string {
    const base = this.pendingSaveType === 'columns' ? 'accounts_view.saved_columns' : 'accounts_view.saved_filters'
    return this.$t(base + '.save_title').toString()
  }

  get saveModalNamePlaceholder(): string {
    const base = this.pendingSaveType === 'columns' ? 'accounts_view.saved_columns' : 'accounts_view.saved_filters'
    return this.$t(base + '.name_placeholder').toString()
  }

  selectAllColumns() {
    this.visibleColumnKeys = ACCOUNT_COLUMNS.map(c => c.key)
  }

  deselectAllColumns() {
    this.visibleColumnKeys = []
  }

  resetColumns() {
    this.visibleColumnKeys = defaultVisibleColumnKeys()
  }

  get canMerge(): boolean {
    const profile = store.state.user?.account?.profile
    return profile === ACCOUNT_PROFILE_ADMIN || profile === ACCOUNT_PROFILE_MANAGER
  }

  get profileOptions() {
    const currentUser = store.state.user
    const isAdmin = currentUser?.account?.profile === ACCOUNT_PROFILE_ADMIN

    return AccountUtils.getProfilesForSelect(isAdmin).map(profile => ({
      value: profile.value,
      label: this.$t(profile.labelKey).toString()
    }))
  }

  get activeOptions() {
    return [
      { value: true, label: this.$t('accounts_view.filters.active_yes') },
      { value: false, label: this.$t('accounts_view.filters.active_no') }
    ]
  }

  get editorialStatusOptions() {
    return [
      { value: 'es usuario editorial', label: this.$t('accounts_view.filters.editorial_status_options.editorial_user') },
      { value: 'no es usuario editorial', label: this.$t('accounts_view.filters.editorial_status_options.not_editorial_user') },
      { value: 'sin revisar', label: this.$t('accounts_view.filters.editorial_status_options.not_reviewed') },
      { value: 'null', label: this.$t('accounts_view.filters.editorial_status_options.empty') }
    ]
  }

  created() {
    this.loadFiltersFromUrl()
  }

  async mounted() {
    await this.loadCountries()
    await this.loadBusinessUnits()
    await this.loadCompanies()
    await this.loadViews()
    // Apply the user's default filter/column views so the page is not a blank slate.
    this.applyDefaultViews()
    await this.loadAccounts()
  }

  // If the user marked a filter and/or column view as default, apply them on entry.
  // A filter default is skipped when the URL already carries explicit filters (deep link wins).
  private applyDefaultViews() {
    const hasUrlFilters = Object.keys(this.$route.query).length > 0
    const defaultFilter = this.savedViews.find(v => v.type === 'filter' && v.isDefault)
    if (defaultFilter && defaultFilter.definition && !hasUrlFilters) {
      this.filters = { ...defaultAccountFilters(), ...(defaultFilter.definition.filters || {}) }
      this.selectedFilterViewId = defaultFilter.id
    }
    const defaultColumns = this.savedViews.find(v => v.type === 'columns' && v.isDefault)
    if (defaultColumns && defaultColumns.definition) {
      const known = new Set(ACCOUNT_COLUMNS.map(c => c.key))
      const cols = (defaultColumns.definition.columns || []).filter(k => known.has(k))
      if (cols.length) {
        this.visibleColumnKeys = cols
      }
    }
  }

  async toggleFilterViewDefault() {
    if (this.selectedFilterViewId === null) return
    await this.setViewDefault(this.selectedFilterViewId, !this.selectedFilterViewIsDefault)
  }

  async toggleColumnViewDefault(view: AccountView) {
    await this.setViewDefault(view.id, !view.isDefault)
  }

  private async setViewDefault(id: number, isDefault: boolean) {
    try {
      await ApiClient.setAccountViewDefault(id, isDefault)
      await this.loadViews()
    } catch (error: any) {
      console.error('Error setting default view:', error)
      this.$bvToast.toast(error.message || this.$t('accounts_view.errors.error_title').toString(), {
        title: this.$t('accounts_view.errors.error_title').toString(),
        variant: 'danger',
        solid: true
      })
    }
  }

  private loadFiltersFromUrl() {
    const query = this.$route.query

    if (typeof query.search === 'string') {
      this.filters.search = query.search
    }
    if (typeof query.profile === 'string') {
      this.filters.profile = query.profile
    }
    if (typeof query.country === 'string') {
      this.filters.country = query.country
    }
    if (typeof query.businessUnit === 'string') {
      this.filters.businessUnit = query.businessUnit
    }
    if (query.active === 'true' || query.active === 'false') {
      this.filters.active = query.active === 'true'
    }
    if (typeof query.editorialStatus === 'string') {
      this.filters.editorialStatus = query.editorialStatus
    }
    if (typeof query.company === 'string') {
      this.filters.company = query.company
    }
    if (typeof query.currentPage === 'string') {
      const page = parseInt(query.currentPage, 10)
      if (!isNaN(page) && page > 0) {
        this.currentPage = page
      }
    }
  }

  private updateUrlQueryParams() {
    const queries: Record<string, string> = {}

    if (this.filters.search) {
      queries.search = this.filters.search
    }
    if (this.filters.profile) {
      queries.profile = this.filters.profile
    }
    if (this.filters.country) {
      queries.country = this.filters.country
    }
    if (this.filters.businessUnit) {
      queries.businessUnit = this.filters.businessUnit
    }
    if (this.filters.active !== null) {
      queries.active = String(this.filters.active)
    }
    if (this.filters.editorialStatus) {
      queries.editorialStatus = this.filters.editorialStatus
    }
    if (this.filters.company) {
      queries.company = this.filters.company
    }
    if (this.currentPage > 1) {
      queries.currentPage = this.currentPage.toString()
    }

    if (JSON.stringify(queries) === JSON.stringify(this.$route.query)) {
      return
    }

    this.$router.replace({ query: queries }).catch(() => { /* duplicated navigation */ })
  }

  async loadCountries() {
    this.countries = await CvValueUtils.getCvValuesByType(CV_VALUE_COUNTRY)
  }

  async loadBusinessUnits() {
    this.businessUnits = await CvValueUtils.getCvValuesByType(CV_VALUE_EDITORIAL_PROJECT_BUSINESS_UNIT)
  }

  async loadCompanies() {
    try {
      this.companies = await ApiClient.getCompanies()
    } catch (error) {
      console.error('Error loading companies:', error)
    }
  }

  getCountryName(countryCode: string | undefined): string {
    if (!countryCode) return '-'
    return CvValueUtils.getCvValueName(countryCode, CV_VALUE_COUNTRY) || '-'
  }

  getBusinessUnitName(businessUnitCode: string | undefined): string {
    if (!businessUnitCode) return '-'
    return CvValueUtils.getCvValueName(businessUnitCode, CV_VALUE_EDITORIAL_PROJECT_BUSINESS_UNIT) || '-'
  }

  getCompanyName(companyId: string | undefined): string {
    if (!companyId) return '-'
    const company = this.companies.find(c => c.id === companyId)
    return company?.name || '-'
  }

  getProfileLabel(profile: string | undefined): string {
    return this.$t(AccountUtils.getProfileTranslationKey(profile)).toString()
  }

  async loadAccounts() {
    try {
      this.loading = true
      this.updateUrlQueryParams()
      const offset = (this.currentPage - 1) * this.perPage
      const result = await ApiClient.searchAccounts(
        this.filters.search,
        this.perPage,
        offset,
        this.filters.profile,
        this.filters.country,
        this.filters.businessUnit,
        this.filters.active,
        this.filters.editorialStatus,
        this.filters.company
      )
      this.accounts = result.items
      this.totalResults = result.totalResults
    } catch (error) {
      console.error('Error loading accounts:', error)
      this.$bvToast.toast(this.$t('accounts_view.errors.load_error').toString(), {
        title: this.$t('accounts_view.errors.error_title').toString(),
        variant: 'danger',
        solid: true
      })
    } finally {
      this.loading = false
    }
  }

  onSearchChange() {
    // Debounce search and reset to first page
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    this.searchTimeout = setTimeout(() => {
      this.currentPage = 1
      this.loadAccounts()
    }, 500)
  }

  onFilterChange() {
    // Reset to first page when filters change
    this.currentPage = 1
    this.loadAccounts()
  }

  onPageChange(page: number) {
    this.currentPage = page
    this.loadAccounts()
  }

  resetFilters() {
    this.filters = defaultAccountFilters()
    this.currentPage = 1
    this.loadAccounts()
  }

  formatDate(dateString: string | undefined) {
    if (!dateString) return '-'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
    } catch (error) {
      return dateString
    }
  }

  openCreateModal() {
    this.selectedAccount = null
    this.$bvModal.show('UpsertAccountModal')
  }

  openEditModal(account: Account) {
    this.selectedAccount = { ...account }
    this.$bvModal.show('UpsertAccountModal')
  }

  openLdapImportModal(account: Account) {
    const modal = this.$refs.ldapImportModal as any
    if (modal) {
      modal.show(account.ldapImport, account.accountId)
    }
  }

  openMergeModal() {
    this.$bvModal.show('MergeAccountsModal')
  }

  onMerged() {
    this.loadAccounts()
  }

  openLdapImportAccountsModal() {
    this.$bvModal.show('LdapImportAccountsModal')
  }

  onLdapImported() {
    this.loadAccounts()
  }

  async confirmDelete(account: Account) {
    const confirmed = await this.$bvModal.msgBoxConfirm(
      this.$t('accounts_view.delete_confirm_body', { account: account.accountId }).toString(),
      {
        title: this.$t('accounts_view.delete_confirm_title').toString(),
        size: 'md',
        okVariant: 'danger',
        okTitle: this.$t('accounts_view.delete_account').toString(),
        cancelTitle: this.$t('button_actions.cancel').toString(),
        centered: true
      }
    )
    if (!confirmed) return

    try {
      await ApiClient.deleteAccount(account.accountId)
      this.$bvToast.toast(this.$t('accounts_view.delete_success', { account: account.accountId }).toString(), {
        title: this.$t('accounts_view.success_title').toString(),
        variant: 'success',
        solid: true
      })
      this.loadAccounts()
    } catch (error: any) {
      console.error('Error deleting account:', error)
      this.$bvToast.toast(error.message || this.$t('accounts_view.delete_error').toString(), {
        title: this.$t('accounts_view.errors.error_title').toString(),
        variant: 'danger',
        solid: true,
        autoHideDelay: 6000
      })
    }
  }

  async loadViews() {
    try {
      this.savedViews = await ApiClient.getAccountViews()
    } catch (error) {
      console.error('Error loading views:', error)
    }
  }

  onFilterViewSelected(id: number | null) {
    if (id === null) return
    const view = this.savedViews.find(v => v.id === id)
    if (!view || !view.definition) return
    // Merge onto defaults so removed filters degrade gracefully and filters
    // added since the preset was saved fall back to their defaults.
    this.filters = { ...defaultAccountFilters(), ...(view.definition.filters || {}) }
    this.currentPage = 1
    this.loadAccounts()
  }

  onColumnViewSelected(id: number | null) {
    if (id === null) return
    const view = this.savedViews.find(v => v.id === id)
    if (!view || !view.definition) return
    // Keep only columns still present in the registry; empty falls back to defaults.
    const known = new Set(ACCOUNT_COLUMNS.map(c => c.key))
    const cols = (view.definition.columns || []).filter(k => known.has(k))
    this.visibleColumnKeys = cols.length ? cols : defaultVisibleColumnKeys()
  }

  openSaveFilterViewModal() {
    this.pendingSaveType = 'filter'
    this.newViewName = ''
    this.$bvModal.show('save-view-modal')
  }

  openSaveColumnViewModal() {
    this.pendingSaveType = 'columns'
    this.newViewName = ''
    this.$bvModal.show('save-view-modal')
  }

  focusViewNameInput() {
    const input = this.$refs.viewNameInput as any
    if (input) input.focus()
  }

  onSaveViewEnter() {
    if (this.newViewName.trim()) {
      this.$bvModal.hide('save-view-modal')
      this.persistView(this.newViewName.trim())
    }
  }

  onSaveViewOk(bvModalEvent: any) {
    const name = this.newViewName.trim()
    if (!name) {
      bvModalEvent.preventDefault()
      return
    }
    this.persistView(name)
  }

  async persistView(name: string) {
    const type = this.pendingSaveType
    const base = type === 'columns' ? 'accounts_view.saved_columns' : 'accounts_view.saved_filters'
    const definition = type === 'columns'
      ? { columns: this.visibleColumnKeys }
      : { filters: this.filters }
    try {
      const saved = await ApiClient.saveAccountView(name, type, definition)
      this.newViewName = ''
      await this.loadViews()
      if (type === 'filter') {
        this.selectedFilterViewId = saved.id
      }
      this.$bvToast.toast(this.$t(base + '.save_success', { name: saved.name }).toString(), {
        title: this.$t('accounts_view.success_title').toString(),
        variant: 'success',
        solid: true
      })
    } catch (error: any) {
      console.error('Error saving preset:', error)
      this.$bvToast.toast(error.message || this.$t(base + '.save_error').toString(), {
        title: this.$t('accounts_view.errors.error_title').toString(),
        variant: 'danger',
        solid: true,
        autoHideDelay: 6000
      })
    }
  }

  deleteSelectedFilterView() {
    if (this.selectedFilterViewId !== null) this.deleteView(this.selectedFilterViewId, 'filter')
  }

  async deleteView(id: number, type: AccountViewType) {
    const view = this.savedViews.find(v => v.id === id)
    if (!view) return
    const base = type === 'columns' ? 'accounts_view.saved_columns' : 'accounts_view.saved_filters'

    const confirmed = await this.$bvModal.msgBoxConfirm(
      this.$t(base + '.delete_confirm_body', { name: view.name }).toString(),
      {
        title: this.$t(base + '.delete_confirm_title').toString(),
        size: 'md',
        okVariant: 'danger',
        okTitle: this.$t(base + '.delete').toString(),
        cancelTitle: this.$t('button_actions.cancel').toString(),
        centered: true
      }
    )
    if (!confirmed) return

    try {
      await ApiClient.deleteAccountView(id)
      if (type === 'filter') {
        this.selectedFilterViewId = null
      }
      await this.loadViews()
      this.$bvToast.toast(this.$t(base + '.delete_success', { name: view.name }).toString(), {
        title: this.$t('accounts_view.success_title').toString(),
        variant: 'success',
        solid: true
      })
    } catch (error: any) {
      console.error('Error deleting preset:', error)
      this.$bvToast.toast(error.message || this.$t(base + '.delete_error').toString(), {
        title: this.$t('accounts_view.errors.error_title').toString(),
        variant: 'danger',
        solid: true,
        autoHideDelay: 6000
      })
    }
  }

  onAccountSaved(account: Account) {
    this.$bvToast.toast(
      this.$t('accounts_view.account_saved_success').toString(),
      {
        title: this.$t('accounts_view.success_title').toString(),
        variant: 'success',
        solid: true
      }
    )
    this.loadAccounts()
  }
}
